import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

type UseAnchoredCoordsParams = {
    isOpen: boolean;
    onClose: () => void;
    popoverWidth: number;
    gap: number;
};

type UseAnchoredCoordsResult = {
    buttonRef: React.RefObject<HTMLButtonElement>;
    coords: { top: number; left: number } | null;
    isPopoverVisible: boolean;
};

export const useAnchoredCoords = ({ isOpen, onClose, popoverWidth, gap }: UseAnchoredCoordsParams): UseAnchoredCoordsResult => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

    const calculateCoords = useCallback((): void => {
        if (!isOpen || !buttonRef.current) {
            setCoords(null);
            return;
        }
        const rect = buttonRef.current.getBoundingClientRect();
        setCoords({
            top: rect.bottom,
            left: rect.left - popoverWidth - gap,
        });
    }, [isOpen, popoverWidth, gap]);

    useLayoutEffect(() => {
        calculateCoords();
    }, [calculateCoords]);

    useEffect(() => {
        if (!isOpen) return undefined;
        window.addEventListener('resize', calculateCoords);
        return () => window.removeEventListener('resize', calculateCoords);
    }, [isOpen, calculateCoords]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const handleBlur = (): void => onClose();
        window.addEventListener('blur', handleBlur);
        return () => window.removeEventListener('blur', handleBlur);
    }, [isOpen, onClose]);

    return { buttonRef: buttonRef, coords: coords, isPopoverVisible: isOpen && coords !== null };
};